import React, { useContext, useMemo } from "react"
import { Button, Col, Form, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { Formik, useFormikContext } from "formik"

import {
  Dimensions,
  convert,
  convertForInput,
  formatIn,
  joinCompound,
  splitCompound,
} from "@/helpers/units"

import {
  getFieldValues,
  getSectionFieldNames,
  getSettings,
  templatePrimaryDocumentFieldRenders,
} from "@/onboarding/features/product/selectors"
import { updateFields } from "@/onboarding/features/product/Slice"
import FormikBootstrap from "@/components/formik-bootstrap"
import { ReservedFields } from "@/onboarding/features/product/models/Product"
import FormikTextList from "@/onboarding/features/product/components/shared/FormikTextList"
import SubmitButton from "@/onboarding/features/product/components/shared/buttons/SubmitButton"
import SubmitAndApplyButton from "@/onboarding/features/product/components/shared/buttons/SubmitAndApplyButton"
import SubmittingSpinner from "@/onboarding/features/product/components/shared/SubmittingSpinner"
import { SubmittingContext } from "@/onboarding/features/product/helpers"
import TrackFormDirty from "@/onboarding/features/product/forms/TrackFormDirty"

// A unit preference: one document field records the display unit for a group
// of value fields. Values are STORED canonical (meters, kilograms); the form
// edits them in the chosen unit, converting on the way in and out.
interface UnitPreference {
  dimension: string
  unitField: string
  valueFields: string[]
  selectLabel: string
  unitNames: Record<string, string>
}

const UNIT_PREFERENCES: UnitPreference[] = [
  {
    dimension: "length",
    unitField: ReservedFields.HEIGHT_UNIT,
    valueFields: [ReservedFields.MINIMUM_HEIGHT, ReservedFields.MAXIMUM_HEIGHT],
    selectLabel: "Height Unit",
    unitNames: { m: "meters", ft: "feet" },
  },
  {
    dimension: "mass",
    unitField: ReservedFields.WEIGHT_UNIT,
    valueFields: [ReservedFields.MINIMUM_WEIGHT, ReservedFields.MAXIMUM_WEIGHT],
    selectLabel: "Weight Unit",
    unitNames: { kg: "kilograms", lb: "pounds" },
  },
]

const currentUnit = (values, pref: UnitPreference): string =>
  (values[pref.unitField] as string) || Dimensions[pref.dimension].canonical

// The display-unit choice for a preference's value fields. Switching converts
// the values in place, so what's on screen stays the same physical quantity.
const UnitSelect = ({ pref, readOnly }): JSX.Element => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>()
  const current = currentUnit(values, pref)

  const onChange = (e) => {
    const next = e.target.value
    if (next === current) return

    pref.valueFields.forEach((name) => {
      const value = Number(values[name])
      if (!Number.isNaN(value)) {
        setFieldValue(name, convertForInput(pref.dimension, value, current, next))
      }
    })
    setFieldValue(pref.unitField, next)
  }

  return (
    <Form.Group>
      <Form.Label>
        <small>{pref.selectLabel}</small>
      </Form.Label>
      <Form.Control as="select" size="sm" value={current} onChange={onChange} disabled={readOnly}>
        {Object.entries(pref.unitNames).map(([unit, unitName]) => (
          <option key={unit} value={unit}>
            {unitName}
          </option>
        ))}
      </Form.Control>
    </Form.Group>
  )
}

// The canonical equivalent in brackets while editing in a non-canonical unit,
// e.g. "(1.22 m)" under a height entered as 4 ft.
const CanonicalHint = ({ pref, name }): JSX.Element => {
  const { values } = useFormikContext<Record<string, unknown>>()
  const unit = currentUnit(values, pref)
  const dim = Dimensions[pref.dimension]
  if (unit === dim.canonical) return null

  const value = Number(values[name])
  if (Number.isNaN(value)) return null

  return (
    <small className="text-muted">
      ({formatIn(pref.dimension, dim.units[unit].toCanonical(value), dim.canonical)})
    </small>
  )
}

// A compound-presented value: two inputs (e.g. ft / in) over ONE formik value
// in the major display unit, so the unit-switch and submit conversions never
// know the pair exists. Editing either part re-joins into the single value.
const CompoundValueInput = ({ pref, name, label, settings }): JSX.Element => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>()
  const unit = currentUnit(values, pref)
  const { compound } = Dimensions[pref.dimension].units[unit]

  const value = Number(values[name])
  const { major, minor } = Number.isNaN(value)
    ? { major: "", minor: "" }
    : splitCompound(pref.dimension, value, unit)

  const onPartChange = (part: "major" | "minor") => (e) => {
    const entered = e.target.value === "" ? 0 : Number(e.target.value)
    if (Number.isNaN(entered)) return

    const pair = {
      major: part === "major" ? entered : Number(major) || 0,
      minor: part === "minor" ? entered : Number(minor) || 0,
    }
    setFieldValue(name, joinCompound(pref.dimension, pair, unit))
  }

  return (
    <Form.Group>
      <Form.Label>
        <small>{label}</small>
      </Form.Label>
      <div className="d-flex gap-2 align-items-center">
        <Form.Control
          type="number"
          min="0"
          size="sm"
          value={major}
          onChange={onPartChange("major")}
          readOnly={!settings.writeable}
        />
        <span>{Dimensions[pref.dimension].units[unit].label}</span>
        <Form.Control
          type="number"
          min="0"
          max={compound.minorPerMajor - 1}
          size="sm"
          value={minor}
          onChange={onPartChange("minor")}
          readOnly={!settings.writeable}
        />
        <span>{compound.minorLabel}</span>
      </div>
    </Form.Group>
  )
}

// One value field under a unit preference: a plain input labelled with the
// current unit, or the compound pair when the unit presents compound (ft),
// plus the canonical bracket hint. Legacy documents without the unit field
// keep the plain canonical-only input.
const UnitValueInput = ({ pref, name, label, settings, hasUnitField }): JSX.Element => {
  const { values } = useFormikContext<Record<string, unknown>>()
  const unit = hasUnitField ? currentUnit(values, pref) : Dimensions[pref.dimension].canonical
  const compound = Boolean(Dimensions[pref.dimension].units[unit].compound)

  return (
    <div>
      {compound ? (
        <CompoundValueInput pref={pref} name={name} label={label} settings={settings} />
      ) : (
        <FormikBootstrap.NumberInput
          name={name}
          label={`${label} (${pref.unitNames[unit]})`}
          readOnly={!settings.writeable}
          showLockedIcon={!settings.writeable}
          showRequired={settings.required}
        />
      )}
      {hasUnitField && <CanonicalHint pref={pref} name={name} />}
    </div>
  )
}

const Edit = ({ setEditing }): JSX.Element => {
  const fieldNames = useSelector(getSectionFieldNames.additionalDetails)
  const initialValues = useSelector(getFieldValues(...fieldNames))
  const fieldsSettings = useSelector(getSettings(...fieldNames))
  const dispatch = useDispatch()
  const [submitting, setSubmitting] = useContext(SubmittingContext)
  const showDrinkingAge = useSelector(templatePrimaryDocumentFieldRenders("minimum_drinking_age"))
  const showDrivingAge = useSelector(templatePrimaryDocumentFieldRenders("minimum_driving_age"))
  const showPregnancy = useSelector(templatePrimaryDocumentFieldRenders("pregnancy_suitable"))
  // Legacy documents that predate a unit field's backfill carry no such
  // field; they keep the plain canonical-only inputs.
  const hasUnitField = (pref: UnitPreference) => fieldNames.includes(pref.unitField)

  // Values are STORED canonical (meters, kilograms); the form edits them in
  // the chosen display unit, so convert on the way in and out.
  const editValues = useMemo(() => {
    const converted = { ...initialValues }
    UNIT_PREFERENCES.forEach((pref) => {
      const { canonical } = Dimensions[pref.dimension]
      const unit = currentUnit(initialValues, pref)
      if (!hasUnitField(pref) || unit === canonical) return

      pref.valueFields.forEach((name) => {
        const value = Number(initialValues[name])
        if (!Number.isNaN(value)) {
          converted[name] = convertForInput(pref.dimension, value, canonical, unit)
        }
      })
    })
    return converted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, fieldNames])

  return (
    <Formik
      enableReinitialize
      initialValues={editValues}
      onSubmit={(values) => {
        const canonical = { ...values }
        UNIT_PREFERENCES.forEach((pref) => {
          const canonicalUnit = Dimensions[pref.dimension].canonical
          const unit = currentUnit(values, pref)
          if (!hasUnitField(pref) || unit === canonicalUnit) return

          pref.valueFields.forEach((name) => {
            const value = Number(values[name])
            if (!Number.isNaN(value)) {
              canonical[name] = convert(pref.dimension, value, unit, canonicalUnit)
            }
          })
        })
        setSubmitting(true)
        dispatch(updateFields({ values: canonical }))
      }}
    >
      {({ handleSubmit }) => (
        <Form className="d-flex flex-column gap-4" onSubmit={handleSubmit}>
          <TrackFormDirty formId="additionalDetails" />
          <Row>
            <Col className="d-flex flex-column gap-2">
              <FormikBootstrap.NumberInput
                name={ReservedFields.MINIMUM_AGE}
                label="Minimum Age (yrs)"
                readOnly={!fieldsSettings[ReservedFields.MINIMUM_AGE].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.MINIMUM_AGE].writeable}
                showRequired={fieldsSettings[ReservedFields.MINIMUM_AGE].required}
              />
              <FormikBootstrap.NumberInput
                name={ReservedFields.MAXIMUM_AGE}
                label="Maximum Age (yrs)"
                readOnly={!fieldsSettings[ReservedFields.MAXIMUM_AGE].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.MAXIMUM_AGE].writeable}
                showRequired={fieldsSettings[ReservedFields.MAXIMUM_AGE].required}
              />
            </Col>
            <Col className="d-flex flex-column gap-2">
              {hasUnitField(UNIT_PREFERENCES[0]) && (
                <UnitSelect
                  pref={UNIT_PREFERENCES[0]}
                  readOnly={!fieldsSettings[ReservedFields.MINIMUM_HEIGHT].writeable}
                />
              )}
              <UnitValueInput
                pref={UNIT_PREFERENCES[0]}
                name={ReservedFields.MINIMUM_HEIGHT}
                label="Minimum Height"
                settings={fieldsSettings[ReservedFields.MINIMUM_HEIGHT]}
                hasUnitField={hasUnitField(UNIT_PREFERENCES[0])}
              />
              <UnitValueInput
                pref={UNIT_PREFERENCES[0]}
                name={ReservedFields.MAXIMUM_HEIGHT}
                label="Maximum Height"
                settings={fieldsSettings[ReservedFields.MAXIMUM_HEIGHT]}
                hasUnitField={hasUnitField(UNIT_PREFERENCES[0])}
              />
            </Col>
            <Col className="d-flex flex-column gap-2">
              {hasUnitField(UNIT_PREFERENCES[1]) && (
                <UnitSelect
                  pref={UNIT_PREFERENCES[1]}
                  readOnly={!fieldsSettings[ReservedFields.MINIMUM_WEIGHT].writeable}
                />
              )}
              <UnitValueInput
                pref={UNIT_PREFERENCES[1]}
                name={ReservedFields.MINIMUM_WEIGHT}
                label="Minimum Weight"
                settings={fieldsSettings[ReservedFields.MINIMUM_WEIGHT]}
                hasUnitField={hasUnitField(UNIT_PREFERENCES[1])}
              />
              <UnitValueInput
                pref={UNIT_PREFERENCES[1]}
                name={ReservedFields.MAXIMUM_WEIGHT}
                label="Maximum Weight"
                settings={fieldsSettings[ReservedFields.MAXIMUM_WEIGHT]}
                hasUnitField={hasUnitField(UNIT_PREFERENCES[1])}
              />
            </Col>
            {(showDrinkingAge || showDrivingAge) && (
              <Col className="d-flex flex-column gap-2">
                {showDrinkingAge && (
                  // @ts-ignore
                  <FormikBootstrap.NumberInput
                    name={ReservedFields.MINIMUM_DRINKING_AGE}
                    label="Minimum Drinking Age"
                  />
                )}
                {showDrivingAge && (
                  // @ts-ignore
                  <FormikBootstrap.NumberInput
                    name={ReservedFields.MINIMUM_DRIVING_AGE}
                    label="Minimum Driving Age"
                  />
                )}
              </Col>
            )}
            <Col className="d-flex flex-column gap-2">
              <FormikBootstrap.TextInput
                name={ReservedFields.MEDICAL_CONDITION_RESTRICTIONS}
                label="Medical Condition Restrictions"
                readOnly={!fieldsSettings[ReservedFields.MEDICAL_CONDITION_RESTRICTIONS].writeable}
                showLockedIcon={
                  !fieldsSettings[ReservedFields.MEDICAL_CONDITION_RESTRICTIONS].writeable
                }
                showRequired={
                  fieldsSettings[ReservedFields.MEDICAL_CONDITION_RESTRICTIONS].required
                }
              />
              {showPregnancy && (
                <FormikBootstrap.SingleSelect
                  name={ReservedFields.PREGNANCY_SUITABLE}
                  label="Suitable for Pregnancy"
                  placeholder="Not specified"
                  options={[
                    { label: "Yes", value: "yes" },
                    { label: "No", value: "no" },
                  ]}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col>
              <label>Does the tour provide:</label>
              <div className="ps-4">
                <FormikBootstrap.Switch
                  name={ReservedFields.INSTRUCTORS_PROVIDED}
                  label="Instructors"
                  disabled={!fieldsSettings[ReservedFields.INSTRUCTORS_PROVIDED].writeable}
                  showLockedIcon={!fieldsSettings[ReservedFields.INSTRUCTORS_PROVIDED].writeable}
                />
                <FormikBootstrap.Switch
                  name={ReservedFields.CHANGING_FACILITIES_PROVIDED}
                  label="Changing Facilities"
                  disabled={!fieldsSettings[ReservedFields.CHANGING_FACILITIES_PROVIDED].writeable}
                  showLockedIcon={
                    !fieldsSettings[ReservedFields.CHANGING_FACILITIES_PROVIDED].writeable
                  }
                />
                <FormikBootstrap.Switch
                  name={ReservedFields.STORAGE_LOCKERS_PROVIDED}
                  label="Storage Lockers"
                  disabled={!fieldsSettings[ReservedFields.STORAGE_LOCKERS_PROVIDED].writeable}
                  showLockedIcon={
                    !fieldsSettings[ReservedFields.STORAGE_LOCKERS_PROVIDED].writeable
                  }
                />
                <FormikBootstrap.Switch
                  name={ReservedFields.TOWELS_PROVIDED}
                  label="Towels For Water Activities"
                  disabled={!fieldsSettings[ReservedFields.TOWELS_PROVIDED].writeable}
                  showLockedIcon={!fieldsSettings[ReservedFields.TOWELS_PROVIDED].writeable}
                />
                <FormikBootstrap.Switch
                  name={ReservedFields.WAIVER_FORMS_REQUIRED}
                  label="Waiver forms to be signed"
                  disabled={!fieldsSettings[ReservedFields.WAIVER_FORMS_REQUIRED].writeable}
                  showLockedIcon={!fieldsSettings[ReservedFields.WAIVER_FORMS_REQUIRED].writeable}
                />
              </div>
            </Col>
            <Col>
              <label>Is the tour suitable for people with:</label>
              <div className="ps-4">
                <FormikBootstrap.Switch
                  name={ReservedFields.WHEELCHAIR_SUITABLE}
                  label="Wheelchairs"
                  disabled={!fieldsSettings[ReservedFields.WHEELCHAIR_SUITABLE].writeable}
                  showLockedIcon={!fieldsSettings[ReservedFields.WHEELCHAIR_SUITABLE].writeable}
                />
                <FormikBootstrap.Switch
                  name={ReservedFields.BLIND_SUITABLE}
                  label="Blindness"
                  disabled={!fieldsSettings[ReservedFields.BLIND_SUITABLE].writeable}
                  showLockedIcon={!fieldsSettings[ReservedFields.BLIND_SUITABLE].writeable}
                />
                <FormikBootstrap.Switch
                  name={ReservedFields.DEAF_SUITABLE}
                  label="Deafness"
                  disabled={!fieldsSettings[ReservedFields.DEAF_SUITABLE].writeable}
                  showLockedIcon={!fieldsSettings[ReservedFields.DEAF_SUITABLE].writeable}
                />
                <FormikBootstrap.Switch
                  name={ReservedFields.WALKING_DIFFICULTY_SUITABLE}
                  label="Walking Difficulties"
                  disabled={!fieldsSettings[ReservedFields.WALKING_DIFFICULTY_SUITABLE].writeable}
                  showLockedIcon={
                    !fieldsSettings[ReservedFields.WALKING_DIFFICULTY_SUITABLE].writeable
                  }
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <FormikTextList
                name={ReservedFields.INCLUDED_EQUIPMENT}
                label="What equipment is included on tour?"
                readOnly={!fieldsSettings[ReservedFields.INCLUDED_EQUIPMENT].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.INCLUDED_EQUIPMENT].writeable}
                showRequired={fieldsSettings[ReservedFields.INCLUDED_EQUIPMENT].required}
              />
            </Col>
            <Col>
              <FormikTextList
                name={ReservedFields.GUEST_GUIDANCE}
                label="What should guests be advised to bring on tour?"
                readOnly={!fieldsSettings[ReservedFields.GUEST_GUIDANCE].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.GUEST_GUIDANCE].writeable}
                showRequired={fieldsSettings[ReservedFields.GUEST_GUIDANCE].required}
              />
            </Col>
          </Row>
          <div className="d-flex gap-2">
            <SubmitButton />
            <SubmitAndApplyButton />
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => setEditing(false)}
              disabled={submitting}
            >
              <i className="fas fa-times me-2" />
              Cancel
            </Button>
            <SubmittingSpinner />
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default Edit
